import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useToggle } from '../../hooks/useToggle';
import { Input } from '../../types/Input';
import { Nullable } from '../../types/Nullable';
import { Option } from '../../types/Option';
import { testHasTextMatch } from '../../utils/testHasTextMatch';
import { typedMemo } from '../../utils/typedMemo';
import { Box } from '../Box/Box';
import { Checkbox } from '../Checkbox/Checkbox';
import { Chip } from '../Chip/Chip';
import { Grid } from '../Grid/Grid';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { Markdown } from '../Markdown/Markdown';

export type CheckboxGroupProps<V> = Input<V, true> & {
  /** Checkbox options */
  options: Option<V>[];
  /** Group columns */
  columns?: number;
  /** CheckboxGroup collapsible content */
  collapsible?: boolean;
  /** Disables labels background */
  disableGroupBackground?: boolean;
  /** Display the groups count and the selected options count */
  enableCount?: boolean;
  /** Group info tooltip */
  info?: string;
  /** Group label */
  label?: string;
  /** Pinned values */
  pins?: V[];
  /** When option.label matches search, render a text match */
  search?: string;
  /** Callback when option label is clicked */
  onOptionClick?: (option: Option<V>) => void;
  /** Enables pin features when callback is provided */
  onUpdatePins?: (
    updatedPins: V[],
    action: { value: V; type: 'pin' | 'unpin' },
  ) => void;
  /** CheckboxGroup mode */
  mode?: 'descriptive';
};

type GroupValues<V> = Nullable<V[]>;

type CheckedValue = Nullable<boolean>;

/**
 * `CheckboxGroup` provides a way to render and use a group of checkboxes.  It has a similar API as `Checkbox` with small props differences (`value`, `onChange`, `options`). `CheckboxGroup` is also implemented with accessibility in mind, rendering as a `HTMLFieldSetElement` of options with an appropriate `HTMLFieldElement` group label.
 */
export const CheckboxGroup = typedMemo(
  <V extends unknown>({
    columns = 1,
    collapsible = false,
    disabled,
    disableGroupBackground = false,
    enableCount,
    info,
    label,
    mode,
    name,
    options,
    pins = [],
    search,
    value = [],
    onChange,
    onOptionClick,
    onUpdatePins,
  }: CheckboxGroupProps<V>) => {
    const intl = useIntl();
    const enabledValues = useMemo(() => {
      return options
        .filter((option) => !option.disabled)
        .map((option) => option.value);
    }, [options]);

    const enablePins = Boolean(onUpdatePins);

    const pinnedValueSet = useMemo(() => new Set(pins), [pins]);

    const isDescriptive = mode === 'descriptive';

    const disabledCheckedOptions = useMemo(() => {
      return options
        .filter((option) => option.disabled && value?.includes(option.value))
        .map((option) => option.value);
    }, [value, options]);

    const [isCollapsed, toggleIsCollapsed] = useToggle();

    if (options.length === 0) {
      return null;
    }

    const handleSingleValueChange =
      (v: V) => (updatedChecked: CheckedValue) => {
        let updatedValue = null;
        if (value === null) {
          updatedValue = updatedChecked ? [v] : [];
        } else {
          updatedValue = updatedChecked
            ? [...value, v]
            : value.filter((v2) => v2 !== v);
        }

        onChange(updatedValue);
      };

    const handleGroupValueChange = (updatedGroupValue: CheckedValue) => {
      const updatedValue = updatedGroupValue
        ? [...enabledValues, ...disabledCheckedOptions]
        : [...disabledCheckedOptions];
      onChange(updatedValue);
    };

    const handleUpdatePins = (optionValue: V) => () => {
      if (enablePins) {
        const isUnpin = pinnedValueSet.has(optionValue);
        const updatedPins = isUnpin
          ? pins.filter((pin) => pin !== optionValue)
          : [...pins, optionValue];

        onUpdatePins?.(updatedPins, {
          value: optionValue,
          type: isUnpin ? 'unpin' : 'pin',
        });

        if (!isUnpin) {
          if (!value?.includes(optionValue)) {
            handleSingleValueChange(optionValue)(true);
          }
        } else {
          if (value?.includes(optionValue)) {
            handleSingleValueChange(optionValue)(false);
          }
        }
      }
    };

    const groupValue = getGroupValue(
      value,
      enabledValues,
      disabledCheckedOptions,
    );

    const filteredOptions = search
      ? options.filter(
          (option) =>
            testHasTextMatch(option.label, search) ||
            testHasTextMatch(option.description ?? '', search),
        )
      : options;

    const optionsCount = filteredOptions.length;
    const valueLength = value?.length;

    const selectedCount = intl.formatMessage(
      {
        id: 'CheckboxGroup.selectedCount',
        defaultMessage: '{valueLength} of {optionsCount} Selected',
        description:
          'CheckboxGroup selected count. Example: valueLength = "3", optionsCount = "10"',
      },
      {
        valueLength,
        optionsCount,
      },
    );

    const legend = label && (
      <Layout
        as="legend"
        styles={componentStyles.legend}
        bg={disableGroupBackground ? null : 'background.quiet'}
        align="center"
        justify="space-between"
      >
        <Checkbox
          disabled={disabled}
          labelVariant="body-bold"
          count={enableCount ? optionsCount : undefined}
          name={`${name}-group`}
          option={{ info, label, value: groupValue }}
          value={groupValue}
          onChange={handleGroupValueChange}
        />
        <Layout align="center" justify="flex-end" spacing={2}>
          {enableCount && <Chip text={selectedCount} mode="tag" />}
          {collapsible && (
            <IconButton
              size="s"
              onClick={toggleIsCollapsed}
              preset={isCollapsed ? 'hide' : 'show'}
            />
          )}
        </Layout>
      </Layout>
    );

    return search && filteredOptions.length === 0 ? null : (
      <Layout
        as="fieldset"
        direction="column"
        spacing={2}
        styles={componentStyles.fieldset}
      >
        {legend}
        <Grid
          columns={isDescriptive ? 2 : columns}
          columnSpacing={6}
          pl={label ? 4 : undefined}
          rowSpacing={2}
        >
          {filteredOptions.map((option, i) => {
            const { disabled: optionDisabled, value: optionValue } = option;
            const isOptionPinned = pinnedValueSet.has(optionValue);
            return (
              <React.Fragment key={i}>
                <Layout
                  align="baseline"
                  justify="space-between"
                  styles={componentStyles.option}
                  display={isCollapsed ? 'none' : undefined}
                >
                  <Checkbox
                    disabled={disabled || optionDisabled}
                    name={name}
                    option={option}
                    search={search}
                    value={value?.includes(optionValue) || false}
                    onChange={handleSingleValueChange(optionValue)}
                    onOptionClick={onOptionClick}
                  />
                  {enablePins && (
                    <Pin
                      isPinned={isOptionPinned}
                      onUpdatePin={handleUpdatePins(optionValue)}
                    />
                  )}
                </Layout>
                {isDescriptive && (
                  <Layout display={isCollapsed ? 'none' : undefined}>
                    <Markdown
                      preset="description"
                      search={search}
                      text={option.description ?? ''}
                    />
                  </Layout>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      </Layout>
    );
  },
);

interface PinProps {
  isPinned: boolean;
  onUpdatePin: () => void;
}

const Pin = ({ isPinned, onUpdatePin }: PinProps) => {
  return (
    <Box
      className="pin"
      styles={[componentStyles.pin, isPinned ? null : componentStyles.hidden]}
    >
      <Icon
        onClick={onUpdatePin}
        icon={isPinned ? 'push-pin-filled' : 'push-pin'}
        color="status.inactive"
      />
    </Box>
  );
};

export const getGroupValue = <V extends unknown>(
  values: GroupValues<V>,
  enabledValues: V[] = [],
  disabledCheckedOptions: V[] = [],
): CheckedValue => {
  if (values === null || values.length === 0) {
    return false;
  } else if (
    values.length ===
    enabledValues.length + disabledCheckedOptions.length
  ) {
    return true;
  } else {
    return null;
  }
};

const componentStyles = {
  fieldset: {
    border: 'none',
    padding: 'unset',
  },
  legend: {
    borderRadius: 'm',
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 2,
    width: '100%',
  },
  option: {
    ':hover .pin': {
      visibility: 'visible',
    },
  },
  pin: {
    display: 'flex',
    marginRight: 1,
  },
  hidden: {
    visibility: 'hidden',
  },
};
